import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import available from "../../../assets/available.svg";
import steering from "../../../assets/images/steering.svg";
import selectedSeatIcon from "../../../assets/selected-seat.svg";
import unavailable from "../../../assets/unavailable.svg";

import Alert from "../../../bootstrap/Alert";
import BusFacilities from "../BusFacilities/index.jsx";
import SeatCount from "../SeatCount/index.jsx";
import fetchSeatData from "../../../helpers/fetch-seat-data";

import {
  BusSeatMapWrapper,
  LeftSideWrapper,
  RightSide,
  SeatCoutWrapper,
  SeatLegend,
  SeatType,
  SeatTypeHeader,
  BusFacilitiesWrapper,
} from "./BusSeatMap.styles.js";

/* -------------------------------------------------------
 * Seat helpers
 * ----------------------------------------------------- */

const getSeatNumber = (seat) =>
  String(seat?.seat_no ?? "").trim();

const isStaffSeat = (seat) =>
  getSeatNumber(seat).toUpperCase().includes("STAFF");

const isVipSeat = (seat) =>
  getSeatNumber(seat).toUpperCase().startsWith("VIP");

const isDriverSeat = (seat) =>
  String(seat?.column_element ?? "").trim().toLowerCase() === "driver";

const isBlankSeat = (seat) =>
  ["blank", "empty"].includes(
    String(seat?.column_element ?? "").trim().toLowerCase()
  );

const isBookableSeat = (seat) =>
  Boolean(getSeatNumber(seat)) &&
  !isStaffSeat(seat) &&
  !isDriverSeat(seat) &&
  !isBlankSeat(seat);

const isSeatBooked = (seat) =>
  seat?.isBooked === true ||
  seat?.is_booked === true ||
  seat?.booked === true;

const getSeatPrice = (seat, tripData) => {
  if (!isBookableSeat(seat)) return 0;

  const rawFare = isVipSeat(seat)
    ? tripData?.vip_fare ?? tripData?.special_fair
    : tripData?.normal_fare ?? tripData?.adult_fair;

  const fare = Number(rawFare);

  return Number.isFinite(fare) && fare >= 0 ? fare : 0;
};

const normalizeSelectedSeat = (seat, tripData) => ({
  ...seat,
  seat_no: getSeatNumber(seat),
  seat_category: isVipSeat(seat) ? "vip" : "normal",
  seat_price: getSeatPrice(seat, tripData),
});

const readSearchInfo = () => {
  try {
    return JSON.parse(localStorage.getItem("searchInfo") || "{}");
  } catch {
    return {};
  }
};

const legendSwatch = (background, border) => ({
  display: "inline-block",
  flexShrink: 0,
  width: "15px",
  height: "15px",
  borderRadius: "3px",
  background,
  border: `1px solid ${border}`,
  boxSizing: "border-box",
});

/* -------------------------------------------------------
 * BusSeatMap component
 * ----------------------------------------------------- */

const BusSeatMap = ({
  tripData,
  busDetails = {},
  selectedSeatNumbers = [],
  setSelectedSeatNumbers,
  luggageSettings,
  setNewLuggageData,
  newLuggageData,
  specialLuggageName,
  setSpecialLuggageName,
  rightPanel,
}) => {
  const {
    setTotalSelectSeat,
    setChildrenSelectSeat,
    setAdultSelectSeat,
    setSpecialSelectSeat,
    setLuggageData,
  } = busDetails;

  const { webSettingData, languageData } = useSelector(
    (state) => state.busLists || {}
  );

  const [seats, setSeats] = useState({ rowData: [] });
  const [date, setDate] = useState("");
  const [isLoadingSeats, setIsLoadingSeats] = useState(true);
  const [seatLoadError, setSeatLoadError] = useState("");
  const [seatSelectionError, setSeatSelectionError] = useState("");

  const maxSeats = Math.max(
    1,
    Number(webSettingData?.max_ticket) || 4
  );

  const currency = webSettingData?.currency_code || "";

  const languageText = useCallback(
    (key, fallback) =>
      languageData?.[key]?.[webSettingData?.language] || fallback,
    [languageData, webSettingData?.language]
  );

  const selectedBookableSeats = useMemo(() => {
    const uniqueSeats = new Map();

    (Array.isArray(selectedSeatNumbers) ? selectedSeatNumbers : []).forEach(
      (seat) => {
        if (!isBookableSeat(seat)) return;

        const seatNo = getSeatNumber(seat);
        if (!seatNo || uniqueSeats.has(seatNo)) return;

        uniqueSeats.set(
          seatNo,
          normalizeSelectedSeat(seat, tripData)
        );
      }
    );

    return Array.from(uniqueSeats.values());
  }, [selectedSeatNumbers, tripData]);

  const seatTotal = useMemo(
    () =>
      selectedBookableSeats.reduce(
        (sum, seat) => sum + getSeatPrice(seat, tripData),
        0
      ),
    [selectedBookableSeats, tripData]
  );

  const normalCount = selectedBookableSeats.filter(
    (seat) => !isVipSeat(seat)
  ).length;

  const vipCount = selectedBookableSeats.filter(isVipSeat).length;

  /* Read the journey date from the existing search data. */

  useEffect(() => {
    const searchInfo = readSearchInfo();
    setDate(searchInfo?.journeydate || "");
  }, []);

  /* -----------------------------------------------------
   * Load the seat layout.
   * --------------------------------------------------- */

  const loadSeats = useCallback(async () => {
    if (!date || !tripData?.subtripId) {
      setSeats({ rowData: [] });
      setIsLoadingSeats(false);
      setSeatLoadError(
        !date
          ? "Journey date is missing. Please search for a trip again."
          : "Trip information is missing."
      );
      return;
    }

    setIsLoadingSeats(true);
    setSeatLoadError("");

    try {
      const result = await fetchSeatData(date, tripData.subtripId);
      const layout = result?.seatlayout;

      if (!Array.isArray(layout?.rowData)) {
        throw new Error("The server returned an invalid seat layout.");
      }

      setSeats(layout);

      const availableSeats = new Map();

      layout.rowData.forEach((row) => {
        (row?.columns || []).forEach((seat) => {
          const seatNo = getSeatNumber(seat);

          if (
            seatNo &&
            isBookableSeat(seat) &&
            !isSeatBooked(seat)
          ) {
            availableSeats.set(seatNo, seat);
          }
        });
      });

      /*
       * Keep only seats that still exist and remain available.
       * Refresh their properties and prices from the server layout.
       */
      if (typeof setSelectedSeatNumbers === "function") {
        setSelectedSeatNumbers((previous) => {
          const unique = new Map();

          (Array.isArray(previous) ? previous : []).forEach(
            (selected) => {
              const seatNo = getSeatNumber(selected);
              const currentSeat = availableSeats.get(seatNo);

              if (!currentSeat || unique.has(seatNo)) return;

              unique.set(
                seatNo,
                normalizeSelectedSeat(currentSeat, tripData)
              );
            }
          );

          return Array.from(unique.values());
        });
      }

      setSeatSelectionError("");
    } catch (error) {
      console.error("Unable to load seat layout:", error);

      setSeats({ rowData: [] });
      setSeatLoadError(
        error?.message || "Unable to load seats. Please try again."
      );
    } finally {
      setIsLoadingSeats(false);
    }
  }, [date, tripData, setSelectedSeatNumbers]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!date || !tripData?.subtripId) {
        setSeats({ rowData: [] });
        setIsLoadingSeats(false);
        setSeatLoadError(
          !date
            ? "Journey date is missing. Please search for a trip again."
            : "Trip information is missing."
        );
        return;
      }

      setIsLoadingSeats(true);
      setSeatLoadError("");

      try {
        const result = await fetchSeatData(date, tripData.subtripId);

        if (cancelled) return;

        const layout = result?.seatlayout;

        if (!Array.isArray(layout?.rowData)) {
          throw new Error("The server returned an invalid seat layout.");
        }

        setSeats(layout);

        const availableSeats = new Map();

        layout.rowData.forEach((row) => {
          (row?.columns || []).forEach((seat) => {
            const seatNo = getSeatNumber(seat);

            if (
              seatNo &&
              isBookableSeat(seat) &&
              !isSeatBooked(seat)
            ) {
              availableSeats.set(seatNo, seat);
            }
          });
        });

        if (typeof setSelectedSeatNumbers === "function") {
          setSelectedSeatNumbers((previous) =>
            (Array.isArray(previous) ? previous : [])
              .filter((selected) =>
                availableSeats.has(getSeatNumber(selected))
              )
              .filter(
                (selected, index, array) =>
                  array.findIndex(
                    (item) =>
                      getSeatNumber(item) === getSeatNumber(selected)
                  ) === index
              )
              .map((selected) =>
                normalizeSelectedSeat(
                  availableSeats.get(getSeatNumber(selected)),
                  tripData
                )
              )
          );
        }
      } catch (error) {
        if (cancelled) return;

        console.error("Unable to load seat layout:", error);
        setSeats({ rowData: [] });
        setSeatLoadError(
          error?.message || "Unable to load seats. Please try again."
        );
      } finally {
        if (!cancelled) setIsLoadingSeats(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [date, tripData?.subtripId, setSelectedSeatNumbers]);

  /* Keep the parent's seat counters synchronized. */

  const syncSeatCounts = useCallback(
    (updatedSeats) => {
      const bookableSeats = (updatedSeats || []).filter(isBookableSeat);

      setTotalSelectSeat?.(bookableSeats.length);
      setAdultSelectSeat?.(
        bookableSeats.filter((seat) => !isVipSeat(seat)).length
      );
      setSpecialSelectSeat?.(
        bookableSeats.filter(isVipSeat).length
      );
      setChildrenSelectSeat?.(0);
    },
    [
      setTotalSelectSeat,
      setAdultSelectSeat,
      setSpecialSelectSeat,
      setChildrenSelectSeat,
    ]
  );

  useEffect(() => {
    syncSeatCounts(selectedBookableSeats);
  }, [selectedBookableSeats, syncSeatCounts]);

  /* Clear stale selection when the journey changes. */

  useEffect(() => {
    if (typeof setSelectedSeatNumbers === "function") {
      setSelectedSeatNumbers([]);
    }

    setSeatSelectionError("");
  }, [date, tripData?.subtripId, setSelectedSeatNumbers]);

  /* -----------------------------------------------------
   * Select or deselect a seat.
   * --------------------------------------------------- */

  const handleSelectSeat = useCallback(
    (seat) => {
      if (!seat) return;

      if (isDriverSeat(seat) || isBlankSeat(seat)) return;

      if (isStaffSeat(seat)) {
        setSeatSelectionError("STAFF seats are not available for booking.");
        return;
      }

      if (isSeatBooked(seat)) {
        setSeatSelectionError("This seat is already booked.");
        return;
      }

      if (typeof setSelectedSeatNumbers !== "function") {
        setSeatSelectionError("Seat selection is currently unavailable.");
        return;
      }

      const seatNo = getSeatNumber(seat);
      if (!seatNo) return;

      const alreadySelected = selectedBookableSeats.some(
        (selected) => getSeatNumber(selected) === seatNo
      );

      let updatedSeats;

      if (alreadySelected) {
        updatedSeats = selectedBookableSeats.filter(
          (selected) => getSeatNumber(selected) !== seatNo
        );
      } else {
        if (selectedBookableSeats.length >= maxSeats) {
          setSeatSelectionError(
            `You can select up to ${maxSeats} seats per booking.`
          );
          return;
        }

        updatedSeats = [
          ...selectedBookableSeats,
          normalizeSelectedSeat(seat, tripData),
        ];
      }

      setSelectedSeatNumbers(updatedSeats);
      syncSeatCounts(updatedSeats);
      setSeatSelectionError("");
    },
    [
      selectedBookableSeats,
      maxSeats,
      setSelectedSeatNumbers,
      syncSeatCounts,
      tripData,
    ]
  );

  const isSelected = useCallback(
    (seatNo) =>
      selectedBookableSeats.some(
        (seat) => getSeatNumber(seat) === String(seatNo ?? "").trim()
      ),
    [selectedBookableSeats]
  );

  const getSeatStatus = (seat) => {
    if (isDriverSeat(seat)) return "steering";
    if (isStaffSeat(seat)) return "staff";
    if (isSeatBooked(seat)) return "unavailable";
    if (isSelected(seat?.seat_no)) return "selected";

    return isVipSeat(seat) ? "vip" : "available";
  };

  const getSeatImage = (seat) => {
    if (isDriverSeat(seat)) return steering;
    if (isStaffSeat(seat) || isSeatBooked(seat)) return unavailable;
    if (isSelected(seat?.seat_no)) return selectedSeatIcon;

    return available;
  };

  const getSeatColor = (seat) => {
    if (isStaffSeat(seat)) return "#bdbdbd";
    if (isDriverSeat(seat)) return "transparent";
    if (isSeatBooked(seat)) return "#d7d7d7";
    if (isSelected(seat?.seat_no)) return "#4c78a8";

    return isVipSeat(seat) ? "#d4af37" : "#2e9d58";
  };

  /* -----------------------------------------------------
   * Render
   * --------------------------------------------------- */

  return (
    <BusSeatMapWrapper>
      <LeftSideWrapper>
        <div
          className="bus-seat-shell"
          style={{
            width: "100%",
            maxWidth: "340px",
            margin: "0 auto",
            border: "6px solid #e5e7eb",
            borderTop: "7px solid #777",
            borderRadius: "10px",
            padding: "8px 5px",
            boxSizing: "border-box",
            background: "#fff",
            overflowX: "auto",
          }}
        >

          {isLoadingSeats ? (
            <p role="status" style={{ textAlign: "center", fontSize: "13px" }}>
              Loading seats...
            </p>
          ) : seatLoadError ? (
            <div style={{ padding: "10px", textAlign: "center" }}>
              <p role="alert">{seatLoadError}</p>
              <button
                type="button"
                onClick={loadSeats}
                style={{ padding: "8px 12px", cursor: "pointer" }}
              >
                Retry
              </button>
            </div>
          ) : seats.rowData.length === 0 ? (
            <p style={{ textAlign: "center", fontSize: "13px" }}>
              No seat layout is available for this trip.
            </p>
          ) : (
            <table
              className="bus_seat_plan_table"
              aria-label="Bus seat selection"
              style={{
                width: "100%",
                borderSpacing: "3px 5px",
                tableLayout: "fixed",
              }}
            >
              <tbody>
                {seats.rowData.map((row, rowIndex) => (
                  <tr key={row?.row_no ?? rowIndex}>
                    {(row?.columns || []).map((seat, columnIndex) => {
                      const seatNo = getSeatNumber(seat);
                      const blank = isBlankSeat(seat);
                      const staff = isStaffSeat(seat);
                      const driver = isDriverSeat(seat);
                      const booked = isSeatBooked(seat);
                      const selected = isSelected(seatNo);
                      const vip = isVipSeat(seat);
                      const disabled = staff || driver || booked || blank;

                      if (blank) {
                        return (
                          <td
                            key={`blank-${rowIndex}-${columnIndex}`}
                            aria-hidden="true"
                          />
                        );
                      }

                      return (
                        <td
                          key={`${seatNo || "empty"}-${columnIndex}`}
                          style={{ textAlign: "center", padding: "1px" }}
                        >
                          {driver ? (
                            <div
                              title="Driver"
                              style={{
                                minHeight: "42px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <img
                                src={steering}
                                alt="Driver"
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          ) : seatNo ? (
                            <button
                              type="button"
                              onClick={() => handleSelectSeat(seat)}
                              disabled={disabled}
                              data-seat-status={getSeatStatus(seat)}
                              aria-pressed={selected}
                              aria-label={`${seatNo}: ${
                                staff
                                  ? "STAFF unavailable"
                                  : booked
                                  ? "booked"
                                  : selected
                                  ? "selected"
                                  : vip
                                  ? "VIP available"
                                  : "normal available"
                              }`}
                              title={
                                staff
                                  ? "STAFF seat — unavailable"
                                  : booked
                                  ? `${seatNo} — booked`
                                  : `${seatNo} — fare: ${getSeatPrice(
                                      seat,
                                      tripData
                                    )}`
                              }
                              style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                maxWidth: "48px",
                                minWidth: "34px",
                                height: "42px",
                                margin: "0 auto",
                                padding: "2px",
                                border: "1px solid #888",
                                borderRadius: "6px",
                                cursor: disabled ? "not-allowed" : "pointer",
                                backgroundColor: getSeatColor(seat),
                                opacity: staff ? 0.8 : 1,
                                boxSizing: "border-box",
                              }}
                            >
                              <img
                                src={getSeatImage(seat)}
                                alt=""
                                aria-hidden="true"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                              <span
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  fontSize: seatNo.length > 5 ? "7px" : "9px",
                                  lineHeight: 1,
                                  fontWeight: 800,
                                  color: "#111",
                                  whiteSpace: "nowrap",
                                  pointerEvents: "none",
                                  textShadow: "0 0 2px white",
                                }}
                              >
                                {seatNo}
                              </span>
                            </button>
                          ) : (
                            <div style={{ minHeight: "42px" }} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </LeftSideWrapper>

      <RightSide>
        {seatSelectionError && (
          <Alert type="danger">{seatSelectionError}</Alert>
        )}

        <section>
          <SeatTypeHeader>
            {languageText("booking_page_seat_legend", "Seat legend")}
          </SeatTypeHeader>

          <SeatLegend>
            <SeatType>
              <span style={legendSwatch("#2e9d58", "#238447")} />
              Normal
            </SeatType>

            <SeatType>
              <span style={legendSwatch("#d4af37", "#b08d22")} />
              VIP
            </SeatType>

            <SeatType>
              <span style={legendSwatch("#4c78a8", "#315b88")} />
              {languageText("booking_page_book", "Selected")}
            </SeatType>

            <SeatType>
              <img src={unavailable} alt="" />
              {languageText("booking_page_unavailable", "Unavailable")}
            </SeatType>

            <SeatType>
              <span style={legendSwatch("#bdbdbd", "#888")} />
              STAFF
            </SeatType>
          </SeatLegend>
        </section>

        <section
          style={{
            marginTop: "10px",
            padding: "10px",
            border: "1px solid #e1e4e8",
            borderRadius: "6px",
            fontSize: "13px",
          }}
        >
          <strong>{languageText("seats", "Selected seats")}</strong>

          {selectedBookableSeats.length === 0 ? (
            <div style={{ marginTop: "5px" }}>None</div>
          ) : (
            selectedBookableSeats.map((seat, index) => (
              <div
                key={getSeatNumber(seat)}
                style={{ marginTop: "5px" }}
              >
                Seat ({index + 1}): {getSeatNumber(seat)}
              </div>
            ))
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "8px",
              marginTop: "8px",
            }}
          >
            <strong>{languageText("ticket_price", "Seat total")}</strong>
            <strong>
              {currency} {seatTotal}
            </strong>
          </div>

          <div style={{ marginTop: "5px", color: "#666" }}>
            {selectedBookableSeats.length} of {maxSeats} seats selected
          </div>
        </section>

        <SeatCoutWrapper>
          <SeatCount
            luggageSettings={luggageSettings}
            totalSelectSeat={selectedBookableSeats.length}
            setTotalSelectSeat={setTotalSelectSeat}
            childrenSelectSeat={0}
            setChildrenSelectSeat={setChildrenSelectSeat}
            specialSelectSeat={vipCount}
            setSpecialSelectSeat={setSpecialSelectSeat}
            setAdultSelectSeat={setAdultSelectSeat}
            adultSelectSeat={normalCount}
            setLuggageData={setLuggageData}
            setNewLuggageData={setNewLuggageData}
            newLuggageData={newLuggageData}
            specialLuggageName={specialLuggageName}
            setSpecialLuggageName={setSpecialLuggageName}
          />
        </SeatCoutWrapper>

        <BusFacilitiesWrapper>
          <BusFacilities facility={tripData?.facility} />
        </BusFacilitiesWrapper>

        {rightPanel}
      </RightSide>
    </BusSeatMapWrapper>
  );
};

export default BusSeatMap;